import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardCheck, Quote } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { CASE_STUDIES, isNamed, displayLabel } from "../lib/caseStudiesData";
import { SITE_URL, COMPANY } from "../lib/seo";

export default function CaseStudiesPage() {
    useSEO({
        title: "Case Studies | Hospitality Consulting Engagements — Optimum Hospitality Solutions",
        description:
            "Selected case studies from recent UK hospitality consulting engagements: property type, brief, intervention and measurable outcome. Representative work across labour, performance, guest experience, pre-opening and multi-site.",
        path: "/case-studies",
    });

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE_URL}/case-studies` },
        ],
    };
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: CASE_STUDIES.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.title,
        })),
    };

    // schema.org Review entries — only emitted for named engagements with a
    // testimonial on file. Each Review is attached to the Service that was
    // delivered, with the named property as `author`.
    const reviewSchemas = CASE_STUDIES.filter(
        (c) => isNamed(c) && c.testimonial && c.testimonial.quote,
    ).map((c) => ({
        "@context": "https://schema.org",
        "@type": "Review",
        itemReviewed: {
            "@type": "Service",
            name: c.title,
            provider: {
                "@type": "ProfessionalService",
                name: COMPANY.legalName,
                url: SITE_URL,
            },
        },
        reviewBody: c.testimonial.quote,
        author: {
            "@type": "Organization",
            name: c.clientName,
        },
        ...(c.testimonial.author
            ? {
                creator: {
                    "@type": "Person",
                    name: c.testimonial.author,
                    ...(c.testimonial.role ? { jobTitle: c.testimonial.role } : {}),
                },
            }
            : {}),
    }));

    return (
        <div data-testid="case-studies-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
            {reviewSchemas.map((r, i) => (
                <script
                    key={`review-schema-${i}`}
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(r) }}
                />
            ))}

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Case Studies</span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                        Recent engagements.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">
                        Selected case studies covering labour productivity, performance improvement, guest experience, pre-opening, food and beverage margin and multi-site standards. Where written client permission is on file the engagement is named; otherwise the property is anonymised. Absolute outcomes are real and rounded.
                    </p>
                </div>
            </section>

            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20 space-y-px">
                    {CASE_STUDIES.map((c, idx) => {
                        const named = isNamed(c);
                        return (
                            <article
                                key={c.slug}
                                data-testid={`case-study-detail-${c.slug}`}
                                data-named={named ? "true" : "false"}
                                className="bg-brand-stone border border-brand-border p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8"
                            >
                                <div className="lg:col-span-4">
                                    <div className="flex items-center gap-3">
                                        <span className="font-display text-3xl text-brand-champagne">{String(idx + 1).padStart(2, "0")}</span>
                                        <ClipboardCheck className="text-brand-champagne" size={20} strokeWidth={1.25} />
                                    </div>
                                    <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-brand-ink/55">
                                        <span>{c.category}</span>
                                        <span className="text-brand-ink/30">·</span>
                                        <span>{c.propertyTypeTag}</span>
                                    </div>
                                    <h2 className="font-display text-2xl lg:text-3xl mt-3 text-brand-navy leading-snug">{c.title}</h2>

                                    {named && c.clientLogoUrl && (
                                        <div className="mt-5">
                                            <img
                                                src={c.clientLogoUrl}
                                                alt={`${c.clientName} logo`}
                                                className="h-12 w-auto object-contain"
                                                loading="lazy"
                                                data-testid={`case-study-logo-${c.slug}`}
                                            />
                                        </div>
                                    )}

                                    <div className="mt-5 space-y-2 text-xs text-brand-ink/65">
                                        <div>
                                            <span className="uppercase tracking-[0.18em] text-brand-ink/50">{named ? "Client" : "Property"}</span>
                                            <br />
                                            <span data-testid={`case-study-label-${c.slug}`} className={named ? "text-brand-navy font-medium" : ""}>
                                                {displayLabel(c)}
                                            </span>
                                        </div>
                                        <div><span className="uppercase tracking-[0.18em] text-brand-ink/50">Engagement length</span><br/>{c.engagementLength}</div>
                                    </div>
                                </div>
                                <div className="lg:col-span-8 space-y-5 text-sm leading-relaxed">
                                    {c.before && (
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Before</div>
                                            <p className="mt-2 text-brand-ink/80">{c.before}</p>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Challenge</div>
                                        <p className="mt-2 text-brand-ink/80">{c.challenge}</p>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Actions Taken</div>
                                        <p className="mt-2 text-brand-ink/80">{c.action}</p>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Results</div>
                                        <p className="mt-2 text-brand-ink/80 font-medium">{c.outcome}</p>
                                    </div>

                                    {named && c.testimonial && c.testimonial.quote && (
                                        <figure
                                            data-testid={`case-study-testimonial-${c.slug}`}
                                            className="mt-6 border-l-2 border-brand-champagne pl-5 py-2"
                                        >
                                            <Quote className="text-brand-champagne" size={20} strokeWidth={1.25} />
                                            <blockquote className="mt-3 font-display text-lg text-brand-navy leading-snug">
                                                &ldquo;{c.testimonial.quote}&rdquo;
                                            </blockquote>
                                            {(c.testimonial.author || c.testimonial.role) && (
                                                <figcaption className="mt-3 text-xs text-brand-ink/65">
                                                    {c.testimonial.author && (
                                                        <span className="text-brand-navy font-medium">{c.testimonial.author}</span>
                                                    )}
                                                    {c.testimonial.author && c.testimonial.role && <span>, </span>}
                                                    {c.testimonial.role}
                                                </figcaption>
                                            )}
                                        </figure>
                                    )}

                                    <div className="pt-2">
                                        <Link
                                            to="/booking"
                                            data-testid={`case-study-cta-${c.slug}`}
                                            className="inline-flex items-center gap-2 text-sm text-brand-navy link-underline"
                                        >
                                            Discuss a similar brief <ArrowUpRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </article>
                        );
                    })}

                    <p className="pt-8 text-xs text-brand-ink/55 italic max-w-3xl">
                        Where the client has granted written permission, the engagement is named. Where permission is not in place, the property is described in anonymised terms and absolute figures are real and rounded. Full named case studies are published with the owner&rsquo;s permission as engagements reach the appropriate point in the post-engagement cycle.
                    </p>
                </div>
            </section>

            <section className="bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-stone">
                            Have a similar challenge in your property?
                        </h2>
                        <p className="mt-5 text-brand-stone/75 max-w-2xl text-lg">
                            Book a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link to="/booking" data-testid="case-studies-bottom-cta" className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white">
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
